/**
 * @fileoverview Main Operator Administration Panel for Buzzer Battle ("De barrio en barrio").
 * Allows stadium operators to configure themes, question sets, preview questions, project views,
 * control the sequential question lifecycle (Launch, Show Options, Turn Assignment, Live Telemetry,
 * Reveal Result, Rebound, Next, Final Podium), manage emergency contingencies, and view match history.
 * 
 * @module components/Admin/Games/BuzzerBattle
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useFloatMessage, MESSAGE_TYPE } from './../../Providers/FloatMessage';
import { SecondaryBase } from './../Commons/SecondaryBase';
import { Confirm } from './../../Commons/Modals/Confirm';
import { PremiumQR } from './../../Commons/PremiumQR';
import { BuzzerBattleThemeModal } from './BuzzerBattle/BuzzerBattleThemeModal';
import { BuzzerBattleQuestionModal } from './BuzzerBattle/BuzzerBattleQuestionModal';

import {
    GetThemes,
    GetTheme,
    DeleteTheme,
    CloneTheme,
    GetQuestionSets,
    GetQuestionSet,
    DeleteQuestionSet,
    CloneQuestionSet,
    GetMatchesHistory,
    ShowCover,
    ShowGame,
    InitMatch,
    LaunchQuestion,
    ShowOptions,
    AssignTurn,
    ManualSelectOption,
    ClearSelectedOption,
    RevealResult,
    GiveRebound,
    NextQuestion,
    PreviousQuestion,
    AdjustPlayerScore,
    ShowFinalResults,
    ResetMatch,
    ForceCorrect,
    ForceIncorrect,
    GetRealtimeState
} from './../../../utilities/BL/BuzzerBattle';

import {
    ConnectWebSocketForBuzzerBattleAdmin,
    DisconnectWebSocketForBuzzerBattleAdmin
} from './../../../utilities/WS/Admin/BuzzerBattleWS';

/**
 * Option letter prefixes.
 */
const OPTION_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

/**
 * Default players template.
 */
const DEFAULT_PLAYERS_SETUP = [
    {
        playerIndex: 0,
        id: 'J1',
        name: 'Jugador 1',
        color: '#00e5ff',
        avatarUrl: '',
        winnerAvatarUrl: '',
        keyCode: 'KeyA',
        score: 0
    },
    {
        playerIndex: 1,
        id: 'J2',
        name: 'Jugador 2',
        color: '#ff0055',
        avatarUrl: '',
        winnerAvatarUrl: '',
        keyCode: 'KeyL',
        score: 0
    }
];

/**
 * Buzzer Battle Operator Admin Panel Component.
 * 
 * @function BuzzerBattle
 * @param {Object} props - Component properties.
 * @param {Function} props.getOutOfTheDynamic - Callback to return to dashboard.
 * @returns {React.ReactElement} Admin panel markup.
 */
export const BuzzerBattle = ({ getOutOfTheDynamic }) => {
    const { showMessage } = useFloatMessage();

    // Loading & Refresh State
    const [isLoadingInitialData, setIsLoadingInitialData] = useState(false);
    const [lastRefreshTimestampString, setLastRefreshTimestampString] = useState('');
    const [webSocketConnectionStatus, setWebSocketConnectionStatus] = useState('connecting');

    // Themes State
    const [availableThemesList, setAvailableThemesList] = useState([]);
    const [selectedThemeId, setSelectedThemeId] = useState(0);
    const [activeThemeObject, setActiveThemeObject] = useState(null);

    // Question Sets State
    const [availableQuestionSetsList, setAvailableQuestionSetsList] = useState([]);
    const [selectedQuestionSetId, setSelectedQuestionSetId] = useState(0);
    const [activeQuestionSetObject, setActiveQuestionSetObject] = useState(null);

    // Player Customization State (Pre-match & In-match)
    const [customPlayersList, setCustomPlayersList] = useState(DEFAULT_PLAYERS_SETUP);

    // Real-time Synchronized Game State
    const [currentRealtimeState, setCurrentRealtimeState] = useState({
        state: 'idle', // idle, cover, game_ready, question_active, turn_active, option_selected, revealed, finished
        themeId: null,
        questionSetId: null,
        themeName: '',
        questionSetTitle: '',
        currentQuestionIndex: 0,
        totalQuestions: 0,
        currentQuestion: null,
        activeTurnPlayerId: null,
        selectedOptionId: null,
        selectedOptionText: '',
        disabledOptionIds: [],
        players: DEFAULT_PLAYERS_SETUP,
        showOptions: true,
        isAnswerRevealed: false,
        isCorrect: null,
        winner: null,
        finalScoresJson: null
    });

    // Match History State
    const [matchesHistoryList, setMatchesHistoryList] = useState([]);

    // Modals State
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [confirmModalConfiguration, setConfirmModalConfiguration] = useState({
        message: '',
        actionToExecute: null,
        yesButtonText: 'Sí, confirmar',
        noButtonText: 'Cancelar'
    });
    const [isThemeModalOpen, setIsThemeModalOpen] = useState(false);
    const [themeToEditObject, setThemeToEditObject] = useState(null);
    const [isQuestionModalOpen, setIsQuestionModalOpen] = useState(false);
    const [questionSetToEditObject, setQuestionSetToEditObject] = useState(null);
    const [isQrModalOpen, setIsQrModalOpen] = useState(false);
    const [buzzerFanUrl, setBuzzerFanUrl] = useState('');
    const [screenDisplayUrl, setScreenDisplayUrl] = useState('');

    /**
     * Resolves media path into an absolute preview URL.
     */
    const resolveMediaUrl = (mediaPath) => {
        if (!mediaPath) return '';
        if (mediaPath.startsWith('http://') || mediaPath.startsWith('https://') || mediaPath.startsWith('blob:')) {
            return mediaPath;
        }
        const apiBaseUrl = (process.env.REACT_APP_API_URL || '').replace('/api/', '/').replace('/api', '/');
        return `${apiBaseUrl}${mediaPath.startsWith('/') ? mediaPath.substring(1) : mediaPath}`;
    };

    /**
     * Handles incoming real-time WebSocket events for Buzzer Battle.
     */
    const handleWebSocketMessageReceived = useCallback((receivedPacket) => {
        if (!receivedPacket || typeof receivedPacket !== 'object') return;

        const packetType = receivedPacket.type;
        const packetState = receivedPacket.state || receivedPacket.data;

        switch (packetType) {
            case 'buzzer_state':
            case 'buzzer_snapshot':
            case 'buzzer_cover_shown':
            case 'buzzer_game_shown':
            case 'buzzer_match_initialized':
            case 'buzzer_question_launched':
            case 'buzzer_options_shown':
            case 'buzzer_turn_assigned':
            case 'buzzer_option_selected':
            case 'buzzer_option_cleared':
            case 'buzzer_result_revealed':
            case 'buzzer_rebound_granted':
            case 'buzzer_next_question':
            case 'buzzer_force_updated':
            case 'buzzer_reset':
                if (packetState) {
                    setCurrentRealtimeState((prev) => ({
                        ...prev,
                        ...packetState
                    }));
                }
                break;

            case 'buzzer_game_over':
            case 'buzzer_final_results':
                if (packetState) {
                    setCurrentRealtimeState((prev) => ({
                        ...prev,
                        ...packetState
                    }));
                }
                loadMatchesHistory();
                break;

            default:
                break;
        }
    }, []);

    /**
     * Initializes data and WebSocket connection on mount.
     */
    useEffect(() => {
        loadAllInitialAdminData();

        ConnectWebSocketForBuzzerBattleAdmin(
            handleWebSocketMessageReceived,
            (newStatus) => setWebSocketConnectionStatus(newStatus)
        );

        return () => {
            DisconnectWebSocketForBuzzerBattleAdmin();
        };
    }, [handleWebSocketMessageReceived]);

    /**
     * Fetches themes, question sets, realtime snapshot, and match history.
     */
    const loadAllInitialAdminData = async () => {
        setIsLoadingInitialData(true);
        try {
            // 1. Fetch Themes
            const themesResponse = await GetThemes();
            if (themesResponse.success && Array.isArray(themesResponse.data)) {
                setAvailableThemesList(themesResponse.data);
                if (themesResponse.data.length > 0 && (!selectedThemeId || selectedThemeId <= 0)) {
                    setSelectedThemeId(themesResponse.data[0].id);
                    setActiveThemeObject(themesResponse.data[0]);
                }
            }

            // 2. Fetch Question Sets
            const setsResponse = await GetQuestionSets();
            if (setsResponse.success && Array.isArray(setsResponse.data)) {
                setAvailableQuestionSetsList(setsResponse.data);
                if (setsResponse.data.length > 0 && (!selectedQuestionSetId || selectedQuestionSetId <= 0)) {
                    setSelectedQuestionSetId(setsResponse.data[0].id);
                    loadQuestionSetDetail(setsResponse.data[0].id);
                }
            }

            // 3. Fetch Realtime State Snapshot
            const realtimeResponse = await GetRealtimeState();
            if (realtimeResponse.success && realtimeResponse.data) {
                setCurrentRealtimeState(realtimeResponse.data);
                if (realtimeResponse.data.themeId) {
                    setSelectedThemeId(realtimeResponse.data.themeId);
                }
                if (realtimeResponse.data.questionSetId) {
                    setSelectedQuestionSetId(realtimeResponse.data.questionSetId);
                }
                if (Array.isArray(realtimeResponse.data.players) && realtimeResponse.data.players.length > 0) {
                    setCustomPlayersList(realtimeResponse.data.players);
                }
            }

            // 4. Fetch History
            await loadMatchesHistory();
        } catch {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error al cargar los datos de Buzzer Battle.' });
        } finally {
            setIsLoadingInitialData(false);
            setLastRefreshTimestampString(
                new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
            );
        }
    };

    /**
     * Loads full question set detail with questions and options.
     */
    const loadQuestionSetDetail = async (setId) => {
        if (!setId || setId <= 0) return;
        try {
            const detailResponse = await GetQuestionSet(setId);
            if (detailResponse.success && detailResponse.data) {
                setActiveQuestionSetObject(detailResponse.data);
            }
        } catch {
            // Non-blocking
        }
    };

    /**
     * Loads the match history log from database (last 20 matches).
     */
    const loadMatchesHistory = async () => {
        try {
            const historyResponse = await GetMatchesHistory(20);
            if (historyResponse.success && Array.isArray(historyResponse.data)) {
                setMatchesHistoryList(historyResponse.data);
            }
        } catch {
            // Non-blocking
        }
    };

    // =========================================================================
    // THEME & QUESTION SET SELECTION HANDLERS
    // =========================================================================

    const handleThemeSelectionChange = (event) => {
        const newlySelectedId = Number(event.target.value) || 0;
        setSelectedThemeId(newlySelectedId);

        const foundTheme = availableThemesList.find((t) => t.id === newlySelectedId);
        setActiveThemeObject(foundTheme || null);

        if (foundTheme) {
            let themePlayers = DEFAULT_PLAYERS_SETUP;
            if (Array.isArray(foundTheme.playersConfig) && foundTheme.playersConfig.length > 0) {
                themePlayers = foundTheme.playersConfig;
            } else if (foundTheme.playersConfigJson) {
                try {
                    const parsed = JSON.parse(foundTheme.playersConfigJson);
                    if (Array.isArray(parsed) && parsed.length > 0) themePlayers = parsed;
                } catch {
                    themePlayers = DEFAULT_PLAYERS_SETUP;
                }
            }

            setCustomPlayersList((prev) => [
                {
                    ...prev[0],
                    name: themePlayers[0]?.name || prev[0].name,
                    color: themePlayers[0]?.color || prev[0].color,
                    avatarUrl: themePlayers[0]?.avatarUrl || prev[0].avatarUrl,
                    winnerAvatarUrl: themePlayers[0]?.winnerAvatarUrl || prev[0].winnerAvatarUrl
                },
                {
                    ...prev[1],
                    name: themePlayers[1]?.name || prev[1].name,
                    color: themePlayers[1]?.color || prev[1].color,
                    avatarUrl: themePlayers[1]?.avatarUrl || prev[1].avatarUrl,
                    winnerAvatarUrl: themePlayers[1]?.winnerAvatarUrl || prev[1].winnerAvatarUrl
                }
            ]);
        }
    };

    const handleQuestionSetSelectionChange = (event) => {
        const newlySelectedId = Number(event.target.value) || 0;
        setSelectedQuestionSetId(newlySelectedId);
        loadQuestionSetDetail(newlySelectedId);
    };

    const handlePlayerCustomizationChange = (playerIndex, fieldName, fieldValue) => {
        setCustomPlayersList((prev) => {
            const updated = [...prev];
            updated[playerIndex] = {
                ...updated[playerIndex],
                [fieldName]: fieldValue
            };
            return updated;
        });
    };

    // =========================================================================
    // THEME CRUD ACTIONS
    // =========================================================================

    const handleOpenCreateThemeModal = () => {
        setThemeToEditObject(null);
        setIsThemeModalOpen(true);
    };

    const handleOpenEditThemeModal = () => {
        const foundTheme = availableThemesList.find((item) => item.id === selectedThemeId);
        setThemeToEditObject(foundTheme || null);
        setIsThemeModalOpen(true);
    };

    const handleCloneSelectedTheme = async () => {
        if (!selectedThemeId || selectedThemeId <= 0) return;
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Clonando tema...' });
        try {
            const cloneResponse = await CloneTheme(selectedThemeId);
            if (cloneResponse.success && cloneResponse.data) {
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Tema clonado exitosamente.' });
                await loadAllInitialAdminData();
                setSelectedThemeId(cloneResponse.data.id);
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: cloneResponse.message || 'Error al clonar tema.' });
            }
        } catch {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error inesperado al clonar tema.' });
        }
    };

    const handleConfirmDeleteTheme = () => {
        if (!selectedThemeId || selectedThemeId <= 0) return;
        const currentTheme = availableThemesList.find((t) => t.id === selectedThemeId);
        const themeName = currentTheme ? currentTheme.name : 'este tema';

        setConfirmModalConfiguration({
            message: `¿Estás seguro de que deseas eliminar permanentemente el tema "${themeName}"?`,
            yesButtonText: 'Sí, eliminar',
            noButtonText: 'Cancelar',
            actionToExecute: async () => {
                showMessage({ type: MESSAGE_TYPE.INFO, text: 'Eliminando tema...' });
                const deleteResponse = await DeleteTheme(selectedThemeId);
                if (deleteResponse.success) {
                    showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Tema eliminado correctamente.' });
                    await loadAllInitialAdminData();
                } else {
                    showMessage({ type: MESSAGE_TYPE.BAD, text: deleteResponse.message || 'Error al eliminar.' });
                }
                setIsConfirmModalOpen(false);
                return { success: true };
            }
        });
        setIsConfirmModalOpen(true);
    };

    // =========================================================================
    // QUESTION SET CRUD ACTIONS
    // =========================================================================

    const handleOpenCreateQuestionModal = () => {
        setQuestionSetToEditObject(null);
        setIsQuestionModalOpen(true);
    };

    const handleOpenEditQuestionModal = () => {
        setQuestionSetToEditObject(activeQuestionSetObject || null);
        setIsQuestionModalOpen(true);
    };

    const handleCloneSelectedQuestionSet = async () => {
        if (!selectedQuestionSetId || selectedQuestionSetId <= 0) return;
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Clonando banco de preguntas...' });
        try {
            const cloneResponse = await CloneQuestionSet(selectedQuestionSetId);
            if (cloneResponse.success && cloneResponse.data) {
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Banco de preguntas clonado con éxito.' });
                await loadAllInitialAdminData();
                setSelectedQuestionSetId(cloneResponse.data.id);
                loadQuestionSetDetail(cloneResponse.data.id);
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: cloneResponse.message || 'Error al clonar banco.' });
            }
        } catch {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error inesperado al clonar banco de preguntas.' });
        }
    };

    const handleConfirmDeleteQuestionSet = () => {
        if (!selectedQuestionSetId || selectedQuestionSetId <= 0) return;
        const currentSet = availableQuestionSetsList.find((s) => s.id === selectedQuestionSetId);
        const setTitle = currentSet ? currentSet.title : 'este banco';

        setConfirmModalConfiguration({
            message: `¿Estás seguro de eliminar el banco de preguntas "${setTitle}" y todas sus preguntas?`,
            yesButtonText: 'Sí, eliminar',
            noButtonText: 'Cancelar',
            actionToExecute: async () => {
                showMessage({ type: MESSAGE_TYPE.INFO, text: 'Eliminando banco de preguntas...' });
                const deleteResponse = await DeleteQuestionSet(selectedQuestionSetId);
                if (deleteResponse.success) {
                    showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Banco de preguntas eliminado.' });
                    await loadAllInitialAdminData();
                } else {
                    showMessage({ type: MESSAGE_TYPE.BAD, text: deleteResponse.message || 'Error al eliminar banco.' });
                }
                setIsConfirmModalOpen(false);
                return { success: true };
            }
        });
        setIsConfirmModalOpen(true);
    };

    // =========================================================================
    // PROJECTION CONTROLS & QR MODAL (CARD 2)
    // =========================================================================

    const handleShowCoverAction = async () => {
        if (!selectedThemeId) {
            showMessage({ type: MESSAGE_TYPE.WARNING, text: 'Selecciona un tema para continuar' });
            return;
        }
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Proyectando Portada de "De barrio en barrio"...' });
        try {
            const coverResponse = await ShowCover();
            if (coverResponse.success) {
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Portada emitida a pantalla gigante.' });
                if (coverResponse.data || coverResponse.state) {
                    setCurrentRealtimeState((prev) => ({ ...prev, ...(coverResponse.data || coverResponse.state) }));
                }
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: coverResponse.message || 'Error al mostrar portada.' });
            }
        } catch {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error de comunicación al mostrar portada.' });
        }
    };

    const handleShowGameAction = async () => {
        if (!selectedThemeId) {
            showMessage({ type: MESSAGE_TYPE.WARNING, text: 'Selecciona un tema para continuar' });
            return;
        }
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Proyectando Escenario de Juego...' });
        try {
            const gameResponse = await ShowGame();
            if (gameResponse.success) {
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Escenario de juego proyectado en pantalla.' });
                if (gameResponse.data || gameResponse.state) {
                    setCurrentRealtimeState((prev) => ({ ...prev, ...(gameResponse.data || gameResponse.state) }));
                }
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: gameResponse.message || 'Error al proyectar escenario.' });
            }
        } catch {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error de comunicación al proyectar escenario.' });
        }
    };

    const handleOpenQrModal = () => {
        const currentHostname = window.location.hostname;
        const currentPort = window.location.port ? `:${window.location.port}` : '';
        const currentProtocol = window.location.protocol;

        const fanUrl = `${currentProtocol}//${currentHostname}${currentPort}/buzzer`;
        const screenUrl = `${currentProtocol}//${currentHostname}${currentPort}/screen`;

        setBuzzerFanUrl(fanUrl);
        setScreenDisplayUrl(screenDisplayUrl || screenUrl);
        setIsQrModalOpen(true);
    };

    // =========================================================================
    // SEQUENTIAL QUESTION CONTROLLER ACTIONS (CARD 3)
    // =========================================================================

    const handleConfirmInitMatch = () => {
        if (!selectedThemeId || selectedThemeId <= 0) {
            showMessage({ type: MESSAGE_TYPE.WARNING, text: 'Selecciona un tema para iniciar la partida.' });
            return;
        }
        if (!selectedQuestionSetId || selectedQuestionSetId <= 0) {
            showMessage({ type: MESSAGE_TYPE.WARNING, text: 'Selecciona un banco de preguntas para iniciar.' });
            return;
        }

        setConfirmModalConfiguration({
            message: '¿Deseas iniciar una nueva partida de Buzzer Battle?',
            yesButtonText: 'Sí, iniciar partida',
            noButtonText: 'Cancelar',
            actionToExecute: async () => {
                await executeInitMatch();
                setIsConfirmModalOpen(false);
                return { success: true };
            }
        });
        setIsConfirmModalOpen(true);
    };

    const executeInitMatch = async () => {
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Inicializando partida...' });
        try {
            const initResponse = await InitMatch(selectedThemeId, selectedQuestionSetId, customPlayersList);
            if (initResponse.success) {
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: '¡Partida inicializada exitosamente!' });
                if (initResponse.data || initResponse.state) {
                    setCurrentRealtimeState((prev) => ({ ...prev, ...(initResponse.data || initResponse.state) }));
                }
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: initResponse.message || 'Error al inicializar la partida.' });
            }
        } catch {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error inesperado al inicializar la partida.' });
        }
    };

    const handleLaunchQuestionAction = async () => {
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Lanzando pregunta a la pantalla y pulsadores...' });
        try {
            const launchResponse = await LaunchQuestion(currentRealtimeState.currentQuestionIndex);
            if (launchResponse.success) {
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Pregunta emitida en vivo.' });
                if (launchResponse.data || launchResponse.state) {
                    setCurrentRealtimeState((prev) => ({ ...prev, ...(launchResponse.data || launchResponse.state) }));
                }
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: launchResponse.message || 'Error al lanzar pregunta.' });
            }
        } catch {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error de comunicación al lanzar pregunta.' });
        }
    };

    const handleShowOptionsAction = async () => {
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Mostrando opciones de respuesta en pantalla...' });
        try {
            const optionsResponse = await ShowOptions();
            if (optionsResponse.success) {
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Opciones de respuesta visibles.' });
                if (optionsResponse.data || optionsResponse.state) {
                    setCurrentRealtimeState((prev) => ({ ...prev, ...(optionsResponse.data || optionsResponse.state) }));
                }
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: optionsResponse.message || 'Error al mostrar opciones.' });
            }
        } catch {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error de comunicación al mostrar opciones.' });
        }
    };

    const handleAssignTurnAction = async (playerIdentifier) => {
        showMessage({ type: MESSAGE_TYPE.INFO, text: `Asignando turno a ${playerIdentifier}...` });
        try {
            const turnResponse = await AssignTurn(playerIdentifier);
            if (turnResponse.success) {
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: `Turno asignado a ${playerIdentifier}.` });
                if (turnResponse.data || turnResponse.state) {
                    setCurrentRealtimeState((prev) => ({ ...prev, ...(turnResponse.data || turnResponse.state) }));
                }
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: turnResponse.message || 'Error al asignar turno.' });
            }
        } catch {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error de comunicación al asignar turno.' });
        }
    };

    const handleClearSelectedOptionAction = async () => {
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Deseleccionando respuesta...' });
        try {
            const clearResponse = await ClearSelectedOption();
            if (clearResponse.success) {
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Respuesta deseleccionada.' });
                if (clearResponse.data || clearResponse.state) {
                    setCurrentRealtimeState((prev) => ({ ...prev, ...(clearResponse.data || clearResponse.state) }));
                }
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: clearResponse.message || 'Error al limpiar opción.' });
            }
        } catch {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error de comunicación al limpiar opción.' });
        }
    };

    const handleRevealResultAction = async () => {
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Revelando veredicto de la respuesta...' });
        try {
            const revealResponse = await RevealResult();
            if (revealResponse.success) {
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Resultado revelado y puntuación actualizada.' });
                if (revealResponse.data || revealResponse.state) {
                    setCurrentRealtimeState((prev) => ({ ...prev, ...(revealResponse.data || revealResponse.state) }));
                }
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: revealResponse.message || 'Error al revelar resultado.' });
            }
        } catch {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error de comunicación al revelar resultado.' });
        }
    };

    const handleGiveReboundAction = async () => {
        const activeId = currentRealtimeState.activeTurnPlayerId || 'J1';
        const nextId = (activeId === 'J1' || activeId === '0') ? 'J2' : 'J1';

        showMessage({ type: MESSAGE_TYPE.INFO, text: `Otorgando rebote a ${nextId}...` });
        try {
            const reboundResponse = await GiveRebound(nextId);
            if (reboundResponse.success) {
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: `¡Rebote concedido a ${nextId}!` });
                if (reboundResponse.data || reboundResponse.state) {
                    setCurrentRealtimeState((prev) => ({ ...prev, ...(reboundResponse.data || reboundResponse.state) }));
                }
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: reboundResponse.message || 'Error al conceder rebote.' });
            }
        } catch {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error de comunicación al conceder rebote.' });
        }
    };

    const handleNextQuestionAction = async () => {
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Avanzando a la siguiente pregunta...' });
        try {
            const nextResponse = await NextQuestion();
            if (nextResponse.success) {
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Avanzado a la siguiente pregunta.' });
                if (nextResponse.data || nextResponse.state) {
                    setCurrentRealtimeState((prev) => ({ ...prev, ...(nextResponse.data || nextResponse.state) }));
                }
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: nextResponse.message || 'Error al avanzar pregunta.' });
            }
        } catch {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error de comunicación al avanzar pregunta.' });
        }
    };

    const handlePreviousQuestionAction = async () => {
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Retrocediendo a la pregunta anterior...' });
        try {
            const prevResponse = await PreviousQuestion();
            if (prevResponse.success) {
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Pregunta anterior cargada.' });
                if (prevResponse.data || prevResponse.state) {
                    setCurrentRealtimeState((prev) => ({ ...prev, ...(prevResponse.data || prevResponse.state) }));
                }
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: prevResponse.message || 'Error al retroceder pregunta.' });
            }
        } catch {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error de comunicación al retroceder pregunta.' });
        }
    };

    const handleAdjustPlayerScoreAction = async (playerId, delta) => {
        const actionLabel = delta > 0 ? 'Aumentando +1 punto' : 'Disminuyendo -1 punto';
        showMessage({ type: MESSAGE_TYPE.INFO, text: `${actionLabel}...` });
        try {
            const adjResponse = await AdjustPlayerScore(playerId, delta);
            if (adjResponse.success) {
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Puntaje actualizado exitosamente.' });
                if (adjResponse.data || adjResponse.state) {
                    setCurrentRealtimeState((prev) => ({ ...prev, ...(adjResponse.data || adjResponse.state) }));
                }
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: adjResponse.message || 'Error al ajustar puntaje.' });
            }
        } catch {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error al ajustar puntaje.' });
        }
    };

    const handleConfirmNextQuestion = () => {
        setConfirmModalConfiguration({
            message: '¿Deseas avanzar a la siguiente pregunta?',
            yesButtonText: 'Sí, siguiente pregunta',
            noButtonText: 'Cancelar',
            actionToExecute: async () => {
                await handleNextQuestionAction();
                setIsConfirmModalOpen(false);
                return { success: true };
            }
        });
        setIsConfirmModalOpen(true);
    };

    const handleConfirmManualSelectOption = (option, letter) => {
        if (!option) return;
        setConfirmModalConfiguration({
            message: `¿Deseas marcar la opción ${letter}: "${option.text}" para el concursante en turno?`,
            yesButtonText: 'Sí, marcar opción',
            noButtonText: 'Cancelar',
            actionToExecute: async () => {
                await handleManualSelectOptionAction(option.id);
                setIsConfirmModalOpen(false);
                return { success: true };
            }
        });
        setIsConfirmModalOpen(true);
    };

    const handleShowFinalResultsAction = async () => {
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Proyectando Podio Final y guardando resultado...' });
        try {
            const resultsResponse = await ShowFinalResults();
            if (resultsResponse.success) {
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: '¡Podio final proyectado y partida registrada!' });
                if (resultsResponse.data || resultsResponse.state) {
                    setCurrentRealtimeState((prev) => ({ ...prev, ...(resultsResponse.data || resultsResponse.state) }));
                }
                await loadMatchesHistory();
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: resultsResponse.message || 'Error al mostrar podio final.' });
            }
        } catch {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error al procesar el podio final.' });
        }
    };

    const handleConfirmShowFinalResults = () => {
        setConfirmModalConfiguration({
            message: '¿Estás seguro de finalizar la partida y proyectar el podio del ganador?',
            yesButtonText: 'Sí, mostrar ganador',
            noButtonText: 'Cancelar',
            actionToExecute: async () => {
                await handleShowFinalResultsAction();
                setIsConfirmModalOpen(false);
                return { success: true };
            }
        });
        setIsConfirmModalOpen(true);
    };

    // =========================================================================
    // EMERGENCY CONTINGENCY CONTROLS (CARD 4)
    // =========================================================================

    const handleForceCorrectAction = async () => {
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Forzando respuesta como CORRECTA (+1 punto)...' });
        try {
            const forceResponse = await ForceCorrect();
            if (forceResponse.success) {
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Respuesta forzada como CORRECTA.' });
                if (forceResponse.data || forceResponse.state) {
                    setCurrentRealtimeState((prev) => ({ ...prev, ...(forceResponse.data || forceResponse.state) }));
                }
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: forceResponse.message || 'Error al forzar correcto.' });
            }
        } catch {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error de comunicación al forzar respuesta.' });
        }
    };

    const handleForceIncorrectAction = async () => {
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Forzando respuesta como INCORRECTA (Strike)...' });
        try {
            const forceResponse = await ForceIncorrect();
            if (forceResponse.success) {
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Respuesta forzada como INCORRECTA.' });
                if (forceResponse.data || forceResponse.state) {
                    setCurrentRealtimeState((prev) => ({ ...prev, ...(forceResponse.data || forceResponse.state) }));
                }
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: forceResponse.message || 'Error al forzar incorrecto.' });
            }
        } catch {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error de comunicación al forzar respuesta.' });
        }
    };

    const handleManualSelectOptionAction = async (optionId) => {
        showMessage({ type: MESSAGE_TYPE.INFO, text: `Seleccionando manualmente opción #${optionId}...` });
        try {
            const manualResponse = await ManualSelectOption(optionId);
            if (manualResponse.success) {
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Opción seleccionada manualmente.' });
                if (manualResponse.data || manualResponse.state) {
                    setCurrentRealtimeState((prev) => ({ ...prev, ...(manualResponse.data || manualResponse.state) }));
                }
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: manualResponse.message || 'Error al seleccionar opción.' });
            }
        } catch {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error de comunicación al seleccionar opción.' });
        }
    };

    const handleConfirmResetMatch = () => {
        setConfirmModalConfiguration({
            message: '¿Estás seguro de reiniciar la sala a estado inicial? Se restablecerán los puntajes y la pregunta actual.',
            yesButtonText: 'Sí, reiniciar sala',
            noButtonText: 'Cancelar',
            actionToExecute: async () => {
                showMessage({ type: MESSAGE_TYPE.INFO, text: 'Reiniciando sala...' });
                try {
                    const resetResponse = await ResetMatch();
                    if (resetResponse.success) {
                        showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Sala restablecida a estado de espera.' });
                        if (resetResponse.data || resetResponse.state) {
                            setCurrentRealtimeState((prev) => ({ ...prev, ...(resetResponse.data || resetResponse.state) }));
                        }
                    } else {
                        showMessage({ type: MESSAGE_TYPE.BAD, text: resetResponse.message || 'Error al reiniciar.' });
                    }
                } catch {
                    showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error inesperado al reiniciar sala.' });
                }
                setIsConfirmModalOpen(false);
                return { success: true };
            }
        });
        setIsConfirmModalOpen(true);
    };

    // =========================================================================
    // RENDER HELPERS
    // =========================================================================

    const renderGameStateBadge = () => {
        const stateKey = currentRealtimeState.state;

        switch (stateKey) {
            case 'idle':
                return <span className="badge bg-secondary fs-6 px-3 py-2">En Espera (Idle)</span>;
            case 'cover':
                return <span className="badge bg-info text-dark fs-6 px-3 py-2">🖼️ Proyectando Portada</span>;
            case 'game_ready':
                return <span className="badge bg-primary fs-6 px-3 py-2">🏟️ Escenario Listo</span>;
            case 'question_active':
                return <span className="badge bg-warning text-dark fs-6 px-3 py-2">❓ Pregunta en Vivo</span>;
            case 'turn_active':
                return <span className="badge bg-danger fs-6 px-3 py-2 animate-pulse">⚡ Turno de Respuesta Activo</span>;
            case 'option_selected':
                return <span className="badge bg-primary fs-6 px-3 py-2">🎯 Opción Marcada</span>;
            case 'revealed':
                return currentRealtimeState.isCorrect ? (
                    <span className="badge bg-success fs-6 px-3 py-2">✅ ¡Respuesta Correcta! (+1)</span>
                ) : (
                    <span className="badge bg-danger fs-6 px-3 py-2">❌ Respuesta Incorrecta (Strike)</span>
                );
            case 'finished':
                return (
                    <span className="badge bg-success fs-6 px-3 py-2">
                        🏆 ¡Finalizado! Ganador: {currentRealtimeState.winner || 'Empate'}
                    </span>
                );
            default:
                return <span className="badge bg-secondary fs-6 px-3 py-2">{stateKey}</span>;
        }
    };

    const currentActiveQuestion = currentRealtimeState.currentQuestion ||
        activeQuestionSetObject?.questions?.[currentRealtimeState.currentQuestionIndex] ||
        null;

    const player1 = currentRealtimeState.players?.[0] || customPlayersList[0] || DEFAULT_PLAYERS_SETUP[0];
    const player2 = currentRealtimeState.players?.[1] || customPlayersList[1] || DEFAULT_PLAYERS_SETUP[1];

    const activeTurnPlayer = (currentRealtimeState.activeTurnPlayerId === 'J1' || currentRealtimeState.activeTurnPlayerId === '0')
        ? player1
        : (currentRealtimeState.activeTurnPlayerId === 'J2' || currentRealtimeState.activeTurnPlayerId === '1')
            ? player2
            : null;

    const shouldShowOptionsBtn = activeThemeObject ? activeThemeObject.showOptions !== false : (currentRealtimeState.showOptions !== false);

    const isLastQuestion = (currentRealtimeState.currentQuestionIndex ?? 0) >= ((currentRealtimeState.totalQuestions || activeQuestionSetObject?.questions?.length || 1) - 1);

    const isMatchActive = currentRealtimeState.state !== 'idle' &&
        currentRealtimeState.state !== 'cover' &&
        currentRealtimeState.state !== 'game_ready';

    return (
        <SecondaryBase actionToExecute={getOutOfTheDynamic}>
            {/* Modal de Confirmación */}
            {isConfirmModalOpen && (
                <Confirm
                    message={confirmModalConfiguration.message}
                    actionToYesButton={confirmModalConfiguration.actionToExecute}
                    textYesButton={confirmModalConfiguration.yesButtonText}
                    textNoButton={confirmModalConfiguration.noButtonText}
                    setShowModal={setIsConfirmModalOpen}
                />
            )}

            {/* Modal de Códigos QR y Enlaces */}
            {isQrModalOpen && (
                <>
                    <div className="cModal__Background" style={{ zIndex: 99998 }} onClick={() => setIsQrModalOpen(false)}></div>
                    <div className="cModal__Container" style={{ zIndex: 99999, maxWidth: '440px', width: '95vw' }}>
                        <div
                            className="cModal__Container__Content position-relative p-0 overflow-hidden"
                            style={{ background: 'transparent', boxShadow: 'none' }}
                        >
                            <button
                                type="button"
                                className="btn btn-sm btn-dark position-absolute top-0 end-0 m-3 rounded-circle p-2"
                                onClick={() => setIsQrModalOpen(false)}
                                style={{ zIndex: 20 }}
                            >
                                <i className="bi bi-x-lg text-white"></i>
                            </button>
                            <div className="bg-dark text-white p-3 rounded-top-4 text-center border-bottom border-secondary">
                                <span className="d-block small text-warning mb-1">
                                    <i className="bi bi-tv me-1"></i> Pantalla Gigante Estadio / Overlay:
                                </span>
                                <a
                                    href={screenDisplayUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="fw-bold text-info small text-break text-decoration-none d-block"
                                >
                                    {screenDisplayUrl} <i className="bi bi-box-arrow-up-right ms-1"></i>
                                </a>
                            </div>
                            <PremiumQR url={buzzerFanUrl} title="DE BARRIO EN BARRIO" />
                        </div>
                    </div>
                </>
            )}

            {/* Modal de Gestión de Temas */}
            {isThemeModalOpen && (
                <BuzzerBattleThemeModal
                    isOpen={isThemeModalOpen}
                    initialThemeData={themeToEditObject}
                    onCloseModal={() => setIsThemeModalOpen(false)}
                    onThemeSavedSuccessfully={(savedTheme) => {
                        loadAllInitialAdminData();
                        if (savedTheme?.id) setSelectedThemeId(savedTheme.id);
                    }}
                />
            )}

            {/* Modal de Gestión de Bancos de Preguntas */}
            {isQuestionModalOpen && (
                <BuzzerBattleQuestionModal
                    isOpen={isQuestionModalOpen}
                    initialQuestionSetData={questionSetToEditObject}
                    onCloseModal={() => setIsQuestionModalOpen(false)}
                    onQuestionSetSavedSuccessfully={(savedSet) => {
                        loadAllInitialAdminData();
                        if (savedSet?.id) {
                            setSelectedQuestionSetId(savedSet.id);
                            loadQuestionSetDetail(savedSet.id);
                        }
                    }}
                />
            )}

            {/* ============================================================ */}
            {/* HEADER DEL PANEL                                             */}
            {/* ============================================================ */}
            <div className="mb-4 text-center text-dark">
                <div className="d-flex align-items-center justify-content-center gap-2">
                    <i className="bi bi-bell-fill text-warning fs-1"></i>
                    <h2 className="text-uppercase fw-bold m-0" style={{ letterSpacing: '2px' }}>
                        De barrio en barrio
                    </h2>
                </div>
                <div className="d-inline-flex align-items-center gap-3 bg-white px-3 py-1 rounded-pill border shadow-sm mt-2 flex-wrap">
                    <span className="small">
                        {webSocketConnectionStatus === 'connected' && <strong className="text-success">🟢 En Línea</strong>}
                        {webSocketConnectionStatus === 'connecting' && <strong className="text-warning">🟡 Conectando...</strong>}
                        {webSocketConnectionStatus === 'reconnecting' && <strong className="text-warning">🟡 Reconectando...</strong>}
                        {webSocketConnectionStatus === 'error' && <strong className="text-danger">🔴 Desconectado</strong>}
                    </span>
                    <span className="small text-muted border-start ps-3">
                        Actualizado: <strong className="text-dark">{lastRefreshTimestampString}</strong>
                    </span>
                    <button
                        type="button"
                        className="btn btn-sm btn-light border rounded-circle p-1 d-flex align-items-center justify-content-center"
                        onClick={loadAllInitialAdminData}
                        title="Refrescar estado"
                        style={{ width: '28px', height: '28px' }}
                    >
                        <i className={`bi bi-arrow-repeat text-primary ${isLoadingInitialData ? 'spinner-border spinner-border-sm border-0' : ''}`}></i>
                    </button>
                </div>
            </div>

            {/* ============================================================ */}
            {/* FILA 1: SETUP Y DISPLAY & LINKS (CARDS 1 & 2)                */}
            {/* ============================================================ */}
            <div className="row g-4 mb-4">
                {/* CARD 1: SETUP (TEMAS, BANCO DE PREGUNTAS Y JUGADORES) */}
                <div className="col-lg-7">
                    <div className="card shadow-sm border-0 rounded-4 h-100 overflow-hidden">
                        <div className="card-header bg-dark text-white p-3 d-flex align-items-center justify-content-between">
                            <h5 className="m-0 fw-bold">
                                <i className="bi bi-sliders text-warning me-2"></i> 1. Configuración de Sala
                            </h5>
                            <span className="badge bg-secondary">Setup</span>
                        </div>
                        <div className="card-body bg-light p-4">
                            {/* Selector de Tema */}
                            <div className="mb-3">
                                <label className="form-label fw-bold small text-muted">
                                    <i className="bi bi-palette-fill me-1 text-primary"></i> Tema Visual Activo:
                                </label>
                                <div className="d-flex align-items-center gap-2 flex-wrap flex-sm-nowrap">
                                    <select
                                        className="form-select fw-bold border-primary shadow-sm"
                                        value={selectedThemeId || ''}
                                        onChange={handleThemeSelectionChange}
                                    >
                                        <option value="">-- Selecciona un tema --</option>
                                        {availableThemesList.map((t) => (
                                            <option key={t.id} value={t.id}>
                                                {t.name} {t.id === selectedThemeId ? '⭐ (Activo)' : ''}
                                            </option>
                                        ))}
                                    </select>
                                    <div className="d-flex gap-1">
                                        <button
                                            type="button"
                                            className="btn btn-outline-primary fw-bold"
                                            onClick={handleOpenEditThemeModal}
                                            disabled={!selectedThemeId}
                                            title="Editar tema"
                                        >
                                            <i className="bi bi-pencil-square"></i>
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary fw-bold"
                                            onClick={handleCloneSelectedTheme}
                                            disabled={!selectedThemeId}
                                            title="Clonar tema"
                                        >
                                            <i className="bi bi-copy"></i>
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-outline-danger fw-bold"
                                            onClick={handleConfirmDeleteTheme}
                                            disabled={!selectedThemeId || availableThemesList.length <= 1}
                                            title="Eliminar tema"
                                        >
                                            <i className="bi bi-trash"></i>
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-success fw-bold"
                                            onClick={handleOpenCreateThemeModal}
                                            title="Crear nuevo tema"
                                        >
                                            <i className="bi bi-plus-lg"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Selector de Banco de Preguntas */}
                            <div className="mb-3">
                                <label className="form-label fw-bold small text-muted">
                                    <i className="bi bi-patch-question-fill me-1 text-success"></i> Banco de Preguntas:
                                </label>
                                <div className="d-flex align-items-center gap-2 flex-wrap flex-sm-nowrap">
                                    <select
                                        className="form-select fw-bold border-success shadow-sm"
                                        value={selectedQuestionSetId || ''}
                                        onChange={handleQuestionSetSelectionChange}
                                    >
                                        <option value="">-- Selecciona un banco de preguntas --</option>
                                        {availableQuestionSetsList.map((s) => (
                                            <option key={s.id} value={s.id}>
                                                {s.title} ({s.questionsCount || s.questions?.length || 0} preguntas)
                                            </option>
                                        ))}
                                    </select>
                                    <div className="d-flex gap-1">
                                        <button
                                            type="button"
                                            className="btn btn-outline-success fw-bold"
                                            onClick={handleOpenEditQuestionModal}
                                            disabled={!selectedQuestionSetId}
                                            title="Editar banco de preguntas"
                                        >
                                            <i className="bi bi-pencil-square"></i>
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary fw-bold"
                                            onClick={handleCloneSelectedQuestionSet}
                                            disabled={!selectedQuestionSetId}
                                            title="Clonar banco"
                                        >
                                            <i className="bi bi-copy"></i>
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-outline-danger fw-bold"
                                            onClick={handleConfirmDeleteQuestionSet}
                                            disabled={!selectedQuestionSetId || availableQuestionSetsList.length <= 1}
                                            title="Eliminar banco"
                                        >
                                            <i className="bi bi-trash"></i>
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-success fw-bold"
                                            onClick={handleOpenCreateQuestionModal}
                                            title="Crear nuevo banco de preguntas"
                                        >
                                            <i className="bi bi-plus-lg"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Personalización rápida de Jugadores (J1 & J2) */}
                            <div className="p-3 bg-white rounded-3 border mb-3">
                                <label className="form-label fw-bold small text-muted mb-2">
                                    <i className="bi bi-people-fill me-1 text-info"></i> Configuración de Participantes (Partida Activa):
                                </label>
                                <div className="row g-2">
                                    <div className="col-md-6">
                                        <div className="input-group input-group-sm">
                                            <span className="input-group-text fw-bold text-white" style={{ backgroundColor: customPlayersList[0]?.color || '#00e5ff' }}>
                                                J1
                                            </span>
                                            <input
                                                type="text"
                                                className="form-control fw-bold"
                                                value={customPlayersList[0]?.name || ''}
                                                onChange={(e) => handlePlayerCustomizationChange(0, 'name', e.target.value)}
                                                placeholder="Nombre Jugador 1"
                                            />
                                            <input
                                                type="color"
                                                className="form-control form-control-color"
                                                value={customPlayersList[0]?.color || '#00e5ff'}
                                                onChange={(e) => handlePlayerCustomizationChange(0, 'color', e.target.value)}
                                                title="Color Jugador 1"
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="input-group input-group-sm">
                                            <span className="input-group-text fw-bold text-white" style={{ backgroundColor: customPlayersList[1]?.color || '#ff0055' }}>
                                                J2
                                            </span>
                                            <input
                                                type="text"
                                                className="form-control fw-bold"
                                                value={customPlayersList[1]?.name || ''}
                                                onChange={(e) => handlePlayerCustomizationChange(1, 'name', e.target.value)}
                                                placeholder="Nombre Jugador 2"
                                            />
                                            <input
                                                type="color"
                                                className="form-control form-control-color"
                                                value={customPlayersList[1]?.color || '#ff0055'}
                                                onChange={(e) => handlePlayerCustomizationChange(1, 'color', e.target.value)}
                                                title="Color Jugador 2"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Acordeón de Previsualización del Banco de Preguntas */}
                            {activeQuestionSetObject && Array.isArray(activeQuestionSetObject.questions) && (
                                <div className="accordion mt-3" id="questionsPreviewAccordion">
                                    <div className="accordion-item border rounded-3 overflow-hidden shadow-sm">
                                        <h2 className="accordion-header" id="headingQuestionsPreview">
                                            <button
                                                className="accordion-button collapsed fw-bold py-2 bg-white"
                                                type="button"
                                                data-bs-toggle="collapse"
                                                data-bs-target="#collapseQuestionsPreview"
                                                aria-expanded="false"
                                                aria-controls="collapseQuestionsPreview"
                                            >
                                                <i className="bi bi-eye-fill text-primary me-2"></i>
                                                Inspeccionar Preguntas ({activeQuestionSetObject.questions.length} preguntas disponibles)
                                            </button>
                                        </h2>
                                        <div
                                            id="collapseQuestionsPreview"
                                            className="accordion-collapse collapse"
                                            aria-labelledby="headingQuestionsPreview"
                                            data-bs-parent="#questionsPreviewAccordion"
                                        >
                                            <div className="accordion-body p-3 bg-light" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                                {activeQuestionSetObject.questions.map((qItem, qIdx) => (
                                                    <div key={qIdx} className="p-2 mb-2 bg-white rounded-3 border">
                                                        <div className="d-flex align-items-center justify-content-between mb-1">
                                                            <strong className="text-primary small">
                                                                #{qIdx + 1}. {qItem.instruction}
                                                            </strong>
                                                            {qItem.imageUrl && (
                                                                <img
                                                                    src={resolveMediaUrl(qItem.imageUrl)}
                                                                    alt="Preview"
                                                                    className="rounded border"
                                                                    style={{ width: '32px', height: '32px', objectFit: 'contain' }}
                                                                />
                                                            )}
                                                        </div>
                                                        <div className="d-flex flex-wrap gap-1 mt-1">
                                                            {qItem.options?.map((opt, oIdx) => (
                                                                <span
                                                                    key={oIdx}
                                                                    className={`badge ${
                                                                        opt.isCorrect
                                                                            ? 'bg-success text-white'
                                                                            : 'bg-light text-dark border'
                                                                    }`}
                                                                >
                                                                    {OPTION_LETTERS[oIdx] || oIdx + 1}. {opt.text}{' '}
                                                                    {opt.isCorrect && <i className="bi bi-check-circle-fill ms-1"></i>}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* CARD 2: DISPLAY & LINKS */}
                <div className="col-lg-5">
                    <div className="card shadow-sm border-0 rounded-4 h-100 overflow-hidden">
                        <div className="card-header bg-dark text-white p-3 d-flex align-items-center justify-content-between">
                            <h5 className="m-0 fw-bold">
                                <i className="bi bi-projector-fill text-info me-2"></i> 2. Proyección y Vistas
                            </h5>
                            {renderGameStateBadge()}
                        </div>
                        <div className="card-body bg-light d-flex flex-column justify-content-between p-4">
                            <p className="small text-muted mb-3">
                                Controla lo que proyecta la pantalla gigante (/screen) e inicia la partida de manera secuencial desde la pregunta 1.
                            </p>
                            <div className="d-grid gap-3">
                                <button
                                    type="button"
                                    className="btn btn-info fw-bold py-2 text-dark shadow-sm"
                                    onClick={handleShowCoverAction}
                                >
                                    <i className="bi bi-image me-1"></i> Mostrar Portada
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary fw-bold py-2 text-white shadow-sm"
                                    onClick={handleShowGameAction}
                                >
                                    <i className="bi bi-tv-fill me-1"></i> Proyectar Pantalla de Juego
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-success fw-bold py-2 shadow-sm text-white"
                                    onClick={handleConfirmInitMatch}
                                >
                                    <i className="bi bi-play-circle-fill me-1"></i> Iniciar Partida
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-outline-dark fw-bold py-2 shadow-sm"
                                    onClick={handleOpenQrModal}
                                >
                                    <i className="bi bi-qr-code-scan me-1"></i> Ver instrucciones de conexión
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-outline-danger fw-bold py-2 shadow-sm"
                                    onClick={handleConfirmResetMatch}
                                >
                                    <i className="bi bi-arrow-counterclockwise me-1"></i> Reiniciar Partida
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ============================================================ */}
            {/* FILA 2: CONTROLADOR SECUENCIAL DE PREGUNTAS (CARD 3)         */}
            {/* ============================================================ */}
            <div className="card shadow-sm border-0 rounded-4 mb-4 overflow-hidden">
                <div className="card-header bg-dark text-white p-3 d-flex align-items-center justify-content-between">
                    <h5 className="m-0 fw-bold">
                        <i className="bi bi-controller text-danger me-2"></i> 3. Controlador Secuencial de Preguntas
                    </h5>
                    <div className="d-flex align-items-center gap-2">
                        <span className="badge bg-primary fs-6 px-3 py-1">
                            Pregunta {(currentRealtimeState.currentQuestionIndex ?? 0) + 1} de{' '}
                            {currentRealtimeState.totalQuestions || activeQuestionSetObject?.questions?.length || 1}
                        </span>
                    </div>
                </div>
                <div className="card-body bg-white p-4">
                    {/* BOTONES SECUENCIALES DEL OPERADOR (ARRIBA) */}
                    <div className="row g-2 mb-4">
                        {shouldShowOptionsBtn && (
                            <div className="col-md-4 col-lg-3">
                                <button
                                    type="button"
                                    className="btn btn-info fw-bold w-100 py-2 shadow-sm text-dark"
                                    onClick={handleShowOptionsAction}
                                >
                                    <i className="bi bi-eye-fill me-1"></i> 2. Mostrar Opciones
                                </button>
                            </div>
                        )}
                        <div className="col-md-4 col-lg-3">
                            <button
                                type="button"
                                className="btn btn-success fw-bold w-100 py-2 shadow-sm text-white"
                                onClick={handleRevealResultAction}
                                disabled={!currentRealtimeState.selectedOptionId && !currentRealtimeState.activeTurnPlayerId}
                            >
                                <i className="bi bi-check2-circle me-1"></i> 3. Revelar Resultado
                            </button>
                        </div>
                        <div className="col-md-4 col-lg-3">
                            <button
                                type="button"
                                className="btn btn-warning fw-bold w-100 py-2 shadow-sm text-dark"
                                onClick={handleGiveReboundAction}
                            >
                                <i className="bi bi-arrow-left-right me-1"></i> Dar el turno al otro jugador
                            </button>
                        </div>
                    </div>

                    {/* COMPARATIVA DE MARCADOR J1 VS J2 (CON BOTONES + / -) */}
                    <div className="row align-items-center g-3 mb-4">
                        {/* JUGADOR 1 */}
                        <div className="col-md-5">
                            <div
                                className={`p-3 rounded-4 border shadow-sm ${
                                    currentRealtimeState.activeTurnPlayerId === 'J1' || currentRealtimeState.activeTurnPlayerId === '0'
                                        ? 'border-3 border-warning bg-warning-subtle'
                                        : 'bg-light'
                                }`}
                                style={{ borderLeft: `8px solid ${player1.color || '#00e5ff'}` }}
                            >
                                <div className="d-flex align-items-center justify-content-between mb-2">
                                    <h5 className="fw-bold m-0" style={{ color: player1.color || '#0099b8' }}>
                                        <i className="bi bi-shield-shaded me-1"></i> {player1.name || 'Jugador 1'}
                                    </h5>
                                    <div className="d-flex align-items-center gap-1">
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-danger fw-bold py-0 px-2 rounded-circle"
                                            onClick={() => handleAdjustPlayerScoreAction('J1', -1)}
                                            title="Restar 1 punto a Jugador 1"
                                            style={{ width: '28px', height: '28px', lineHeight: '1' }}
                                        >
                                            -
                                        </button>
                                        <span className="badge bg-dark fs-6 px-3">{player1.score || 0} pts</span>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-success fw-bold py-0 px-2 rounded-circle"
                                            onClick={() => handleAdjustPlayerScoreAction('J1', 1)}
                                            title="Sumar 1 punto a Jugador 1"
                                            style={{ width: '28px', height: '28px', lineHeight: '1' }}
                                        >
                                            +
                                        </button>
                                    </div>
                                </div>
                                <div className="d-flex align-items-center gap-3">
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-primary fw-bold px-3 shadow-sm"
                                        onClick={() => handleAssignTurnAction('J1')}
                                    >
                                        <i className="bi bi-hand-index-thumb-fill me-1"></i> Dar Turno a J1
                                    </button>
                                    {(currentRealtimeState.activeTurnPlayerId === 'J1' || currentRealtimeState.activeTurnPlayerId === '0') && (
                                        <span className="badge bg-danger animate-pulse">¡TIENE EL TURNO!</span>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* VS CENTRAL */}
                        <div className="col-md-2 text-center">
                            <span className="badge bg-dark fs-4 px-3 py-2 rounded-pill shadow">VS</span>
                        </div>

                        {/* JUGADOR 2 */}
                        <div className="col-md-5">
                            <div
                                className={`p-3 rounded-4 border shadow-sm ${
                                    currentRealtimeState.activeTurnPlayerId === 'J2' || currentRealtimeState.activeTurnPlayerId === '1'
                                        ? 'border-3 border-warning bg-warning-subtle'
                                        : 'bg-light'
                                }`}
                                style={{ borderRight: `8px solid ${player2.color || '#ff0055'}` }}
                            >
                                <div className="d-flex align-items-center justify-content-between mb-2">
                                    <div className="d-flex align-items-center gap-1">
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-danger fw-bold py-0 px-2 rounded-circle"
                                            onClick={() => handleAdjustPlayerScoreAction('J2', -1)}
                                            title="Restar 1 punto a Jugador 2"
                                            style={{ width: '28px', height: '28px', lineHeight: '1' }}
                                        >
                                            -
                                        </button>
                                        <span className="badge bg-dark fs-6 px-3">{player2.score || 0} pts</span>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-success fw-bold py-0 px-2 rounded-circle"
                                            onClick={() => handleAdjustPlayerScoreAction('J2', 1)}
                                            title="Sumar 1 punto a Jugador 2"
                                            style={{ width: '28px', height: '28px', lineHeight: '1' }}
                                        >
                                            +
                                        </button>
                                    </div>
                                    <h5 className="fw-bold m-0" style={{ color: player2.color || '#d90044' }}>
                                        {player2.name || 'Jugador 2'} <i className="bi bi-shield-shaded ms-1"></i>
                                    </h5>
                                </div>
                                <div className="d-flex align-items-center justify-content-end gap-3">
                                    {(currentRealtimeState.activeTurnPlayerId === 'J2' || currentRealtimeState.activeTurnPlayerId === '1') && (
                                        <span className="badge bg-danger animate-pulse">¡TIENE EL TURNO!</span>
                                    )}
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-danger fw-bold px-3 shadow-sm"
                                        onClick={() => handleAssignTurnAction('J2')}
                                    >
                                        <i className="bi bi-hand-index-thumb-fill me-1"></i> Dar Turno a J2
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* TELEMETRÍA EN VIVO DEL PARTICIPANTE */}
                    {activeTurnPlayer && (
                        <div className="p-3 mb-4 bg-dark text-white rounded-4 shadow-sm border border-secondary d-flex align-items-center justify-content-between flex-wrap gap-3">
                            <div className="d-flex align-items-center gap-3">
                                <div
                                    className="rounded-circle p-2 d-flex align-items-center justify-content-center"
                                    style={{ backgroundColor: activeTurnPlayer.color, width: '45px', height: '45px' }}
                                >
                                    <i className="bi bi-bell-fill text-dark fs-4"></i>
                                </div>
                                <div>
                                    <span className="small text-warning d-block">Participante en Turno:</span>
                                    <h5 className="fw-bold m-0 text-white">{activeTurnPlayer.name}</h5>
                                </div>
                                <div className="border-start border-secondary ps-3 ms-2">
                                    <span className="small text-muted d-block">Opción Marcada en Móvil:</span>
                                    <strong className="text-info fs-6">
                                        {currentRealtimeState.selectedOptionText
                                            ? currentRealtimeState.selectedOptionText
                                            : 'Esperando selección en pantalla móvil...'}
                                    </strong>
                                </div>
                            </div>
                            <div>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-warning fw-bold"
                                    onClick={handleClearSelectedOptionAction}
                                    disabled={!currentRealtimeState.selectedOptionId}
                                >
                                    <i className="bi bi-arrow-counterclockwise me-1"></i> Deseleccionar / Cambiar Opción
                                </button>
                            </div>
                        </div>
                    )}

                    {/* CONTENIDO DE LA PREGUNTA ACTIVA CON BOTÓN LATERAL */}
                    <div className="p-3 bg-light rounded-4 border">
                        <div className="row g-3 align-items-stretch">
                            {/* Columna Izquierda: Imagen, Enunciado y Opciones clickeables */}
                            <div className="col-12 col-lg-9 col-xl-10 d-flex flex-column justify-content-between">
                                <div>
                                    {/* Navegación previa a la partida (solo antes de iniciar) */}
                                    {!isMatchActive && (
                                        <div className="d-flex align-items-center justify-content-between mb-3 pb-2 border-bottom flex-wrap gap-2">
                                            <span className="small text-muted fw-bold">
                                                <i className="bi bi-eye-fill me-1 text-primary"></i> Vista previa de preguntas (Antes de iniciar):
                                            </span>
                                            <div className="btn-group btn-group-sm shadow-sm">
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-secondary fw-bold"
                                                    onClick={handlePreviousQuestionAction}
                                                    disabled={(currentRealtimeState.currentQuestionIndex ?? 0) <= 0}
                                                >
                                                    <i className="bi bi-chevron-left me-1"></i> Pregunta Anterior
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-primary fw-bold"
                                                    onClick={handleNextQuestionAction}
                                                    disabled={isLastQuestion}
                                                >
                                                    Siguiente Pregunta <i className="bi bi-chevron-right ms-1"></i>
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    <div className="row align-items-center g-3 mb-3">
                                        {currentActiveQuestion?.imageUrl && (
                                            <div className="col-auto text-center">
                                                <img
                                                    src={resolveMediaUrl(currentActiveQuestion.imageUrl)}
                                                    alt="Pregunta"
                                                    className="rounded-3 border shadow-sm"
                                                    style={{ width: '120px', height: '120px', objectFit: 'contain', backgroundColor: '#000' }}
                                                    onError={(e) => {
                                                        e.target.style.display = 'none';
                                                    }}
                                                />
                                            </div>
                                        )}
                                        <div className="col">
                                            <div className="d-flex align-items-center gap-2 mb-1">
                                                <span className="badge bg-secondary">Enunciado en Vivo:</span>
                                                <span className="badge bg-dark">
                                                    Pregunta {(currentRealtimeState.currentQuestionIndex ?? 0) + 1} de {currentRealtimeState.totalQuestions || activeQuestionSetObject?.questions?.length || 1}
                                                </span>
                                            </div>
                                            <h4 className="fw-bold text-dark m-0">
                                                {currentActiveQuestion?.instruction || 'No hay pregunta seleccionada o en curso.'}
                                            </h4>
                                        </div>
                                    </div>
                                </div>

                                {/* Opciones disponibles e interactivas para el operador */}
                                {currentActiveQuestion?.options && currentActiveQuestion.options.length > 0 && (
                                    <div className="row g-2 pt-2 border-top">
                                        {currentActiveQuestion.options.map((opt, oIdx) => {
                                            const letter = OPTION_LETTERS[oIdx] || oIdx + 1;
                                            const isSelected = currentRealtimeState.selectedOptionId === opt.id;
                                            const isFailed = currentRealtimeState.disabledOptionIds?.includes(opt.id);
                                            const isCorrectOption = opt.isCorrect === true;

                                            return (
                                                <div key={oIdx} className="col-md-6 col-xl-3">
                                                    <button
                                                        type="button"
                                                        className={`btn text-start w-100 p-2 rounded-3 border fw-bold transition h-100 d-flex flex-column justify-content-between ${
                                                            isSelected
                                                                ? 'btn-warning text-dark border-warning shadow-sm animate-pulse'
                                                                : isFailed
                                                                    ? 'btn-light text-muted text-decoration-line-through disabled'
                                                                    : opt.isCorrect && currentRealtimeState.isAnswerRevealed
                                                                        ? 'btn-success text-white'
                                                                        : isCorrectOption
                                                                            ? 'btn-outline-success bg-white text-dark border-2'
                                                                            : 'btn-outline-dark bg-white text-dark'
                                                        }`}
                                                        disabled={isFailed}
                                                        onClick={() => handleConfirmManualSelectOption(opt, letter)}
                                                        title={isFailed ? 'Opción ya fallada' : 'Clic para marcar opción (con confirmación)'}
                                                    >
                                                        <div className="d-flex align-items-center justify-content-between w-100 mb-1">
                                                            <span className="badge bg-dark text-white me-1">
                                                                {letter}
                                                            </span>
                                                            {isCorrectOption && (
                                                                <span className="badge bg-success text-white small" title="Respuesta Correcta en el banco">
                                                                    <i className="bi bi-check-circle-fill me-1"></i> Correcta
                                                                </span>
                                                            )}
                                                        </div>
                                                        <span className="d-block text-break">{opt.text}</span>
                                                        {isSelected && (
                                                            <span className="badge bg-dark text-warning mt-1 w-100 text-center">
                                                                ⭐ Opción Marcada
                                                            </span>
                                                        )}
                                                        {isFailed && (
                                                            <span className="badge bg-danger text-white mt-1 w-100 text-center">
                                                                ❌ Fallada
                                                            </span>
                                                        )}
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            {/* Columna Derecha: Botón Lateral de Alto Completo (Siguiente Pregunta / Mostrar Ganador) */}
                            <div className="col-12 col-lg-3 col-xl-2 d-flex">
                                {isLastQuestion ? (
                                    <button
                                        type="button"
                                        className="btn btn-dark text-warning fw-bold w-100 shadow-sm d-flex flex-column align-items-center justify-content-center p-3 border-2 border-warning"
                                        onClick={handleConfirmShowFinalResults}
                                        style={{ minHeight: '120px' }}
                                    >
                                        <i className="bi bi-trophy-fill fs-1 mb-2 text-warning animate-bounce"></i>
                                        <span className="fs-5 text-center lh-sm">Mostrar Ganador</span>
                                        <small className="text-white-50 mt-1">Podio Final</small>
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        className="btn btn-primary text-white fw-bold w-100 shadow-sm d-flex flex-column align-items-center justify-content-center p-3"
                                        onClick={handleConfirmNextQuestion}
                                        style={{ minHeight: '120px' }}
                                    >
                                        <i className="bi bi-chevron-double-right fs-1 mb-2"></i>
                                        <span className="fs-5 text-center lh-sm">Siguiente Pregunta</span>
                                        <small className="text-white-50 mt-1">Avanzar ❯</small>
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ============================================================ */}
            {/* FILA 3: HISTORIAL DE PARTIDAS (CARD 4)                       */}
            {/* ============================================================ */}
            <div className="card shadow-sm border-0 rounded-4 mb-4 overflow-hidden">
                <div className="card-header bg-dark text-white p-3 d-flex align-items-center justify-content-between">
                    <h5 className="m-0 fw-bold">
                        <i className="bi bi-clock-history text-warning me-2"></i> 4. Historial de Partidas (Últimas 20)
                    </h5>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-light"
                        onClick={loadMatchesHistory}
                    >
                        <i className="bi bi-arrow-repeat me-1"></i> Actualizar Historial
                    </button>
                </div>
                <div className="card-body bg-white p-0">
                    <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        <table className="table table-striped table-hover align-middle mb-0">
                            <thead className="table-light sticky-top">
                                <tr>
                                    <th># Partida</th>
                                    <th>Fecha / Hora</th>
                                    <th>Ganador</th>
                                    <th>Puntuaciones Finales</th>
                                    <th>Tema</th>
                                    <th>Banco de Preguntas</th>
                                </tr>
                            </thead>
                            <tbody>
                                {matchesHistoryList.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="text-center py-4 text-muted">
                                            No se han registrado partidas de "De barrio en barrio" todavía.
                                        </td>
                                    </tr>
                                ) : (
                                    matchesHistoryList.map((matchItem) => {
                                        let scoresDisplay = matchItem.finalScoresJson || '0 - 0';
                                        try {
                                            const parsed = JSON.parse(matchItem.finalScoresJson);
                                            if (Array.isArray(parsed)) {
                                                scoresDisplay = parsed.map((p) => `${p.name || 'J'}: ${p.score || 0} pts`).join(' | ');
                                            }
                                        } catch {
                                            // Fallback
                                        }

                                        return (
                                            <tr key={matchItem.id}>
                                                <td className="fw-bold font-monospace">#{matchItem.id}</td>
                                                <td className="small text-muted">
                                                    {new Date(matchItem.playedAt).toLocaleString()}
                                                </td>
                                                <td>
                                                    <span className="badge bg-success fw-bold fs-6">
                                                        🏆 {matchItem.winner || 'Empate'}
                                                    </span>
                                                </td>
                                                <td className="fw-bold text-primary small">{scoresDisplay}</td>
                                                <td className="small text-muted">{matchItem.themeName || '-'}</td>
                                                <td className="small text-muted">{matchItem.questionSetTitle || '-'}</td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </SecondaryBase>
    );
};
